import { useState, useEffect, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import FullLogo from '../assets/ROILogo.png';
import BubbleMenu from './BubbleMenu';
import { useAppData } from '../contexts/AppDataContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const { appData, loading } = useAppData();
  const { language, setLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const t = appData?.content?.nav?.[language] || {};

  const logoEl = useMemo(() => (
          <img
            src={FullLogo}
            alt="ROI Agency"
            width={160}
            height={40}
            className="h-8 md:h-10 object-contain"
          />
  ), []);

  // Map navigation items to BubbleMenu format (memoized to avoid prop churn on scroll).
  const menuItems = useMemo(() => ([
    {
      label: t.home || 'Home',
      href: '#home',
      ariaLabel: t.home || 'Home',
      rotation: -8,
      hoverStyles: { bgColor: '#c0fc00', textColor: '#10161B' }
    },
    {
      label: t.services || 'Services',
      href: '#services',
      ariaLabel: t.services || 'Services',
      rotation: 8,
      hoverStyles: { bgColor: '#c0fc00', textColor: '#10161B' }
    },
    {
      label: t.techProducts || 'Tech Products',
      href: '#tech-products',
      ariaLabel: t.techProducts || 'Tech Products',
      rotation: 0,
      hoverStyles: { bgColor: '#c0fc00', textColor: '#10161B' }
    },
    {
      label: t.work || 'Work',
      href: '#portfolio',
      ariaLabel: t.work || 'Work',
      rotation: 8,
      hoverStyles: { bgColor: '#c0fc00', textColor: '#10161B' }
    },
    {
      label: t.about || 'About',
      href: '#about',
      ariaLabel: t.about || 'About',
      rotation: -8,
      hoverStyles: { bgColor: '#c0fc00', textColor: '#10161B' }
    },
    {
      label: t.team || 'Team',
      href: '#team',
      ariaLabel: t.team || 'Team',
      rotation: 8,
      hoverStyles: { bgColor: '#c0fc00', textColor: '#10161B' }
    },
    {
      label: t.join || 'Join',
      href: '#contact',
      ariaLabel: t.join || 'Join',
      rotation: -8,
      hoverStyles: { bgColor: '#c0fc00', textColor: '#10161B' }
    }
  ]), [t.home, t.services, t.techProducts, t.work, t.about, t.team, t.join]);

  if (loading || !appData || !t) {
    return (
      <div className="h-20 w-full fixed top-0 left-0"></div>
    );
  }

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
  };
  

  const menuBg = '#10161B';
  const menuContentColor = '#ffffff';

  return (
    <div 
      dir="ltr"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-dark/95 backdrop-blur-md shadow-lg' 
          : 'bg-transparent'
      }`}
    >
      {/* BubbleMenu */}
      <BubbleMenu
        logo={logoEl}
        items={menuItems}
        menuAriaLabel="Toggle Navigation"
        menuBg={menuBg}
        menuContentColor={menuContentColor}
        useFixedPosition={true}
        animationEase="back.out(1.2)"
        animationDuration={0.35}
        staggerDelay={0.08}
      />

      {/* Language Toggle - Positioned separately - Always on the right */}
      <div className="absolute top-8 right-2 sm:right-8 flex items-center gap-1 sm:gap-2 z-[1002] max-w-[calc(100vw-1rem)]">
        {/* Language Toggle */}
        <div className="flex items-center gap-1 sm:gap-2 bg-dark rounded-full px-1 sm:px-2 py-1 shadow-[0_4px_16px_rgba(0,0,0,0.12)] flex-shrink-0 min-w-0">
          <button
            onClick={() => handleLanguageChange('ar')}
            aria-label="Switch to Arabic"
            aria-pressed={language === 'ar'}
            className={`min-w-[44px] min-h-[44px] px-2 sm:px-3 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              language === 'ar'
                ? 'bg-primary text-dark'
                : 'text-gray-300 hover:text-primary'
            }`}
            type="button"
          >
            ar
          </button>
          <button
            onClick={() => handleLanguageChange('en')}
            aria-label="Switch to English"
            aria-pressed={language === 'en'}
            className={`min-w-[44px] min-h-[44px] px-2 sm:px-3 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
              language === 'en'
                ? 'bg-primary text-dark'
                : 'text-gray-300 hover:text-primary'
            }`}
            type="button"
          >
            en
          </button>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
